import type { ExperimentMetrics, VariantMetrics } from '../types';
import { formatRpv, pluralize, variantLabel } from './variants';

// A challenger "likely" beats (or loses to) control past these odds.
export const LIKELY = 0.9;

export type ReadoutTone = 'none' | 'early' | 'likely' | 'close';

export interface Readout {
  tone: ReadoutTone;
  label: string; // short: "Early read", "Result"
  text: string; // "Challenger ahead, not certain yet"
}

// A trial test judges only who's past their trial; everything else, everyone.
const judgedVisits = (v: VariantMetrics): number => v.settled?.visits ?? v.visits;
const judgedStarters = (v: VariantMetrics): number => v.settled?.starters ?? v.starters;

/** What the results compare: prices, or whole offers in a trial test. */
export const offerWord = (metrics: Pick<ExperimentMetrics, 'trialTest'> | null | undefined): 'offer' | 'price' =>
  metrics?.trialTest ? 'offer' : 'price';

/** A trial test's visitors all arrived too recently to have been charged yet. */
const stillInTrials = (metrics: ExperimentMetrics): boolean =>
  Boolean(metrics.trialTest && metrics.trialTest.countedVisitors === 0);

/** "$1.20 per visitor"; while a trial test's trials run, who has started. */
export function revenueLine(v: VariantMetrics, currency: string): string {
  if (v.visits === 0) return 'No visitors yet';
  if (judgedVisits(v) === 0) return `${v.starters} started`;
  return `${formatRpv(v.revenuePerVisitor, currency)} per visitor`;
}

/** "3 paid · 8 still in trial · 1 didn't pay" for a trial test; null with nobody started. */
export function trialProgressLine(v: VariantMetrics): string | null {
  const progress = v.settled?.progress;
  if (!progress) return null;
  const parts = [
    progress.paid > 0 ? `${progress.paid} paid` : null,
    // A no-trial offer's buyer is only here until their payment lands.
    progress.stillInTrial > 0 && (v.trialDays ?? 0) > 0 ? `${progress.stillInTrial} still in trial` : null,
    progress.didNotPay > 0 ? `${progress.didNotPay} didn't pay` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** How a trial test counts, in one line. Null on any other test. */
export function trialTestNote(metrics: ExperimentMetrics): string | null {
  if (!metrics.trialTest) return null;
  const { countedVisitors } = metrics.trialTest;
  return `Results wait for first payments after the free trial. ${countedVisitors.toLocaleString('en-US')} of ${pluralize(metrics.totalVisits, 'visitor')} counted so far.`;
}

function bestChallenger(variants: VariantMetrics[]): VariantMetrics | null {
  const challengers = variants.filter((v) => !v.isControl);
  if (challengers.length === 0) return null;
  return challengers.reduce((best, v) =>
    (v.probabilityToBeatControl ?? 0) > (best.probabilityToBeatControl ?? 0) ? v : best
  );
}

/**
 * One-line, honest read of a test: never names a winner during an early
 * read (below the minimum sample), only who is ahead.
 */
export function readTest(metrics: ExperimentMetrics, offerClosed = false): Readout {
  const read = baseRead(metrics);
  // Nothing more can sell, so nothing here is "early" any more.
  return offerClosed && read.label === 'Early read' ? { ...read, label: 'So far' } : read;
}

function baseRead(metrics: ExperimentMetrics): Readout {
  const { variants } = metrics;
  if (metrics.totalVisits === 0) {
    return { tone: 'none', label: 'No visitors yet', text: 'Waiting for the first visitor' };
  }
  if (!metrics.revenueComparable) {
    return { tone: 'early', label: 'Your call', text: "Can't be judged by revenue yet" };
  }
  if (stillInTrials(metrics)) {
    return { tone: 'early', label: 'Early read', text: 'Waiting for trials to end' };
  }
  const started = variants.reduce((sum, v) => sum + (v.starters ?? 0), 0);
  if (metrics.totalPayments === 0 && started === 0) {
    return { tone: 'early', label: 'Early read', text: 'No sales yet' };
  }
  if (metrics.totalPayments === 0) {
    return { tone: 'early', label: 'Early read', text: 'Trials started, no payments yet' };
  }
  const challenger = bestChallenger(variants);
  const control = variants.find((v) => v.isControl) ?? null;
  if (!challenger || !control) {
    return { tone: 'early', label: 'Early read', text: 'Not enough data yet' };
  }
  // Sales recorded with no money in them: nothing to compare yet.
  if (variants.every((v) => v.revenuePerVisitor === 0)) {
    return { tone: 'early', label: 'Early read', text: 'No revenue yet' };
  }

  const p = challenger.probabilityToBeatControl ?? 0.5;
  const name = variantLabel(challenger.variantName);

  if (metrics.earlyRead) {
    const leader = challenger.revenuePerVisitor > control.revenuePerVisitor ? name : 'Control';
    const tied =
      challenger.revenuePerVisitor === control.revenuePerVisitor || variants.some((v) => v.visits === 0);
    return {
      tone: 'early',
      label: 'Early read',
      text: tied ? 'Too early to tell' : `${leader} ahead, not certain yet`,
    };
  }
  if (p >= LIKELY) return { tone: 'likely', label: 'Result', text: `${name} likely beats Control` };
  if (p <= 1 - LIKELY) return { tone: 'likely', label: 'Result', text: `Control likely beats ${name}` };
  return { tone: 'close', label: 'Result', text: 'Too close to call' };
}

/** Per-spot read for the "By spot" table, from visitors and RPV only. */
export function readSpot(
  visits: number,
  controlRpv: number,
  challengerRpv: number,
  minVisitors: number
): string {
  if (visits === 0) return 'No visitors yet';
  if (visits < Math.min(100, minVisitors)) return 'Too few visitors';
  if (controlRpv === challengerRpv) return 'Too close to call';
  const leader = challengerRpv > controlRpv ? 'Challenger' : 'Control';
  return visits < minVisitors * 2 ? `${leader} ahead, early` : `${leader} ahead`;
}

/**
 * The sample a reliable answer needs, from the thresholds the stats use
 * (`earlyRead` clears once every variant has `minVisitorsPerVariant` unique
 * visitors and `minConversionsPerVariant` starters, and someone has paid).
 * Progress is half visitors, half sales, each counted per variant up to its
 * own target, so one busy variant can't fill the bar, and the bar never reads
 * full while the read is still early.
 */
export interface SampleTarget {
  perVariant: number; // visitors each price needs
  conversions: number; // sales each price needs
  total: number; // perVariant × variants
  percent: number; // 0..100
}

export function sampleTarget(metrics: ExperimentMetrics, variantCount = metrics.variants.length): SampleTarget {
  const perVariant = metrics.minVisitorsPerVariant;
  const count = Math.max(1, variantCount);
  const total = perVariant * count;
  const conversions = metrics.minConversionsPerVariant;
  const visitorsCounted = metrics.variants.reduce((sum, v) => sum + Math.min(judgedVisits(v), perVariant), 0);
  const salesCounted = metrics.variants.reduce((sum, v) => sum + Math.min(judgedStarters(v), conversions), 0);
  const progress =
    total > 0 && conversions > 0 ? (visitorsCounted / total + salesCounted / (conversions * count)) / 2 : 0;
  const percent = Math.min(100, Math.round(progress * 100));
  return {
    perVariant,
    conversions,
    total,
    percent: metrics.earlyRead ? Math.min(99, percent) : percent,
  };
}

/**
 * What's still missing for a reliable answer, by price: "Control needs 3 more
 * sales. Challenger needs 120 more visitors and 7 more sales." Null when
 * nothing is.
 */
export function sampleShortfall(metrics: ExperimentMetrics, saleWord: string): string | null {
  const lines = metrics.variants.flatMap((v) => {
    const visitors = metrics.minVisitorsPerVariant - judgedVisits(v);
    const sales = metrics.minConversionsPerVariant - judgedStarters(v);
    const parts = [
      visitors > 0 ? `${pluralize(visitors, 'more visitor')}` : null,
      sales > 0 ? `${pluralize(sales, `more ${saleWord}`)}` : null,
    ].filter(Boolean);
    return parts.length > 0 ? [`${variantLabel(v.variantName)} needs ${parts.join(' and ')}.`] : [];
  });
  if (lines.length > 0) return lines.join(' ');
  return metrics.earlyRead && metrics.totalPayments === 0
    ? 'Trials have started, but nobody has paid yet.'
    : null;
}

/**
 * The one "Bought" the results show (cards, per spot, chart, Home): unique
 * buyers who started, a membership (trials included) or a payment, over
 * unique visitors. Tests with a trial say so in the label.
 */
export function boughtLabel(
  offerType: string | undefined,
  variants: Array<{ trialDays?: number }>
): string {
  if (offerType === 'unlock') return 'Unlocked';
  return variants.some((v) => (v.trialDays ?? 0) > 0) ? 'Start rate' : 'Bought';
}

/**
 * What the sample target counts per offer, singular. Starters include anyone
 * who began a free trial, so on a test with a trial they aren't all sales.
 */
export function saleWordFor(
  offerType: string | undefined,
  variants: Array<{ trialDays?: number }>
): 'unlock' | 'start' | 'sale' {
  if (offerType === 'unlock') return 'unlock';
  return variants.some((v) => (v.trialDays ?? 0) > 0) ? 'start' : 'sale';
}

/** Percent of visitors who started, from counts, at most 100; null with no visitors. */
export function startedPercent(starters: number, visitors: number): number | null {
  return visitors > 0 ? Math.min(100, (starters / visitors) * 100) : null;
}

/** Highest revenue per visitor; Control wins ties (and an empty test). */
export function leaderByRpv(variants: VariantMetrics[]): VariantMetrics | null {
  if (variants.length === 0) return null;
  const control = variants.find((v) => v.isControl) ?? variants[0];
  return variants.reduce(
    (best, v) => (v.revenuePerVisitor > best.revenuePerVisitor ? v : best),
    control
  );
}

/**
 * The price actually ahead, or null when none is: nobody has earned anything, the
 * top is a tie, or a price has no visitors to compare yet.
 */
export function clearLeader(variants: VariantMetrics[]): VariantMetrics | null {
  if (variants.length === 0 || variants.some((v) => judgedVisits(v) === 0)) return null;
  const leader = leaderByRpv(variants);
  if (!leader || leader.revenuePerVisitor <= 0) return null;
  const tied = variants.some(
    (v) => v.variantId !== leader.variantId && v.revenuePerVisitor === leader.revenuePerVisitor
  );
  return tied ? null : leader;
}

/** Relative revenue-per-visitor lift vs Control; null when Control earned nothing or a price had no visitors. */
export function liftVsControl(variants: VariantMetrics[], variantId: string): number | null {
  const control = variants.find((v) => v.isControl);
  const variant = variants.find((v) => v.variantId === variantId);
  if (!control || !variant || control.revenuePerVisitor <= 0 || judgedVisits(variant) === 0) return null;
  return (variant.revenuePerVisitor - control.revenuePerVisitor) / control.revenuePerVisitor;
}

/** 0.18 → "+18%", -0.05 → "−5%". */
export function formatLift(lift: number): string {
  const pct = Math.round(lift * 100);
  if (pct === 0) return '±0%';
  return pct > 0 ? `+${pct}%` : `−${Math.abs(pct)}%`;
}

/**
 * Chance the given variant beats every other price on revenue per visitor,
 * from the P(beat Control) the stats return: a challenger's own number;
 * for Control, 1 − the best challenger's. Null before probabilities exist.
 */
export function chanceItLeads(metrics: ExperimentMetrics, variantId: string): number | null {
  if (!metrics.probabilityReady) return null;
  const variant = metrics.variants.find((v) => v.variantId === variantId);
  if (!variant) return null;
  if (!variant.isControl) return variant.probabilityToBeatControl;
  const challengers = metrics.variants.filter((v) => !v.isControl);
  if (challengers.length === 0) return null;
  return 1 - Math.max(...challengers.map((v) => v.probabilityToBeatControl ?? 0));
}

/** Why a test that differs in billing, or an older one in both price and trial, gets no revenue verdict. */
export const NOT_JUDGED_BY_REVENUE =
  "A free trial or a different billing period changes when money comes in, so revenue per visitor can't fairly judge this comparison yet. The choice is yours.";

export interface EndSummary {
  lift: number | null; // vs Control, revenue per visitor; null for Control or no revenue
  chance: number | null; // chanceItLeads at the end
  decided: string; // how decided it was, in words
}

/** The final read of an ended test, for its winner. */
/** "a 62%", "an 87%": 8, 11, 18 and the eighties are said with a vowel first. */
function aPercent(pct: number): string {
  return `${pct === 8 || pct === 11 || pct === 18 || (pct >= 80 && pct < 90) ? 'an' : 'a'} ${pct}%`;
}

export function endSummary(metrics: ExperimentMetrics, winnerId: string): EndSummary {
  if (!metrics.revenueComparable) {
    return {
      lift: null,
      chance: null,
      decided:
        "A free trial or a different billing period changes when money comes in, so revenue per visitor couldn't judge this comparison. This was your call.",
    };
  }
  const winner = metrics.variants.find((v) => v.variantId === winnerId);
  const lift = winner && !winner.isControl ? liftVsControl(metrics.variants, winnerId) : null;
  const chance = chanceItLeads(metrics, winnerId);
  const pct = chance === null ? null : Math.round(chance * 100);
  const word = offerWord(metrics);
  const decided =
    metrics.totalVisits === 0
      ? 'It ended before anyone visited, so there was nothing to compare.'
      : pct === null
        ? `It ended before there was enough data to compare the ${word}s.`
        : metrics.earlyRead
          ? `It ended on an early read: ${aPercent(pct)} chance it was the better ${word}, so treat it as your call rather than a result.`
          : pct >= Math.round(LIKELY * 100)
            ? `Decided: ${aPercent(pct)} chance it was the better ${word}.`
            : `Close: ${aPercent(pct)} chance it was the better ${word}.`;
  return { lift, chance, decided };
}

/** "Live today" / "Live 1 day" / "Ran 12 days"; null without a start date. */
export function liveFor(
  startedAt: string | null | undefined,
  prefix: 'Live' | 'Ran',
  until: Date = new Date()
): string | null {
  if (!startedAt) return null;
  const days = Math.floor((until.getTime() - new Date(startedAt).getTime()) / 86_400_000);
  if (days < 1) return prefix === 'Live' ? 'Live today' : 'Ran under a day';
  return `${prefix} ${days} ${days === 1 ? 'day' : 'days'}`;
}
